import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  alpha,
  Divider,
  Stack,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FolderIcon from "@mui/icons-material/Folder";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import { GlobalContext } from "../../context/GlobalContext";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const drawerWidth = 250;

const UserSidebar = ({
  lang,
  theme,
  isMobile,
  selectedMenu,
  mobileOpen,
  handleDrawerToggle,
  handleMenuClick,
  isDark,
}) => {
  const { handleLogout, web } = React.useContext(GlobalContext);

  const siteName = web?.site_name || lang.appName || "TorrentCloud";
  const logoFile = web?.site_logo;

  // Auto-initials fallback (e.g. "TorrentCloud" → "TC")
  const initials = siteName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const menuItems = [
    {
      id: "dashboard",
      text: lang.dashboard || "Dashboard",
      icon: <DashboardIcon fontSize="small" />,
    },
    {
      id: "download-torrent",
      text: lang.downloadTorrent || "Download Torrent",
      icon: <CloudDownloadIcon fontSize="small" />,
    },
    {
      id: "my-files",
      text: lang.myFiles || "My Files",
      icon: <FolderIcon fontSize="small" />,
    },
    {
      id: "my-plan",
      text: lang.myPlan || "My Plan",
      icon: <CardMembershipIcon fontSize="small" />,
    },
    {
      id: "billing",
      text: lang.billingOrders || "Billing & Orders",
      icon: <ReceiptIcon fontSize="small" />,
    },
    {
      id: "support",
      text: lang.support || "Support",
      icon: <SupportAgentIcon fontSize="small" />,
    },
    {
      id: "profile",
      text: lang.profile || "Profile",
      icon: <AccountCircleIcon fontSize="small" />,
    },
  ];

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* ── Logo / Brand ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Logo box — real image or gradient fallback */}
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              overflow: "hidden",
              flexShrink: 0,
              ...(logoFile
                ? {
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    bgcolor: "transparent",
                  }
                : {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
                      theme.palette.primary.main,
                      0.7,
                    )} 100%)`,
                    boxShadow: `0 4px 14px ${alpha(
                      theme.palette.primary.main,
                      0.4,
                    )}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }),
            }}
          >
            {logoFile ? (
              <Box
                component="img"
                src={`/media/${logoFile}`}
                alt={siteName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              // Fallback: CloudDownload icon (keeps original user-facing feel)
              // but swap to initials if you prefer consistency with admin sidebar
              <CloudDownloadIcon sx={{ color: "#fff", fontSize: 20 }} />
            )}
          </Box>

          {/* Site name + subtitle */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
            >
              {siteName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              Cloud Downloader
            </Typography>
          </Box>
        </Stack>

        {/* Mobile close button */}
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* ── Nav Items ────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1.5, px: 1.5 }}>
        <Typography
          variant="caption"
          color="text.disabled"
          fontWeight={700}
          sx={{
            px: 1,
            fontSize: "0.65rem",
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            display: "block",
            mb: 1,
          }}
        >
          Navigation
        </Typography>
        <List disablePadding>
          {menuItems.map((item) => {
            const isSelected = selectedMenu === item.id;
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleMenuClick(item.id)}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    minHeight: 42,
                    position: "relative",
                    "&.Mui-selected": {
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.15,
                      )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                      color: theme.palette.primary.main,
                      "& .MuiListItemIcon-root": {
                        color: theme.palette.primary.main,
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "20%",
                        bottom: "20%",
                        width: 3,
                        borderRadius: "0 3px 3px 0",
                        bgcolor: theme.palette.primary.main,
                      },
                    },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 34,
                      color: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: 13.5,
                      fontWeight: isSelected ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* ── Bottom: Logout ───────────────────────────────────────────────── */}
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <ListItemButton
          onClick={() => handleLogout(false)}
          sx={{
            borderRadius: 2,
            color: "error.main",
            py: 0.8,
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34, color: "error.main" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang.logout || "Logout"}
            primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth, border: "none" },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            border: "none",
            boxShadow: isDark
              ? `1px 0 0 ${alpha("#ffffff", 0.06)}`
              : "1px 0 0 #f0f0f0",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default UserSidebar;
